export class AudioManager {
  private audioContext: AudioContext | null = null;
  private sounds: Map<string, AudioBuffer> = new Map();
  private masterVolume = 0.85;

  private getAudioContext(): AudioContext | null {
    if (!this.audioContext) {
      try {
        this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
        this.generateSounds();
      } catch (error) {
        console.log('Web Audio API not supported:', error);
        return null;
      }
    }
    return this.audioContext;
  }

  private generateSounds() {
    if (!this.audioContext) return;

    // Generar sonido de movimiento (beep corto)
    this.generateTone('move', 800, 0.15, 'square');

    // Generar sonido de victoria (melodía ascendente)
    this.generateVictorySound();

    // Generar sonido de derrota (melodía descendente)
    this.generateDefeatSound();

    // Generar sonido de empate (tono neutro)
    this.generateTone('tie', 440, 0.35, 'sine');

    // Generar sonido de conexión (sonido bienvenida)
    this.generateConnectSound();

    // Generar sonido de click de botón
    this.generateTone('click', 600, 0.08, 'square');

    // Generar sonido de notificación de chat
    this.generateTone('message', 1200, 0.12, 'square');
  }

  private generateTone(name: string, frequency: number, duration: number, type: OscillatorType = 'square') {
    if (!this.audioContext) return;

    const sampleRate = this.audioContext.sampleRate;
    const numSamples = Math.floor(sampleRate * duration);
    const buffer = this.audioContext.createBuffer(1, numSamples, sampleRate);
    const channelData = buffer.getChannelData(0);

    for (let i = 0; i < numSamples; i++) {
      const t = i / sampleRate;
      // Decay más suave para que el sonido sea bien audible
      const envelope = Math.exp(-t * 3);
      const wave = type === 'square'
        ? Math.sign(Math.sin(2 * Math.PI * frequency * t))
        : Math.sin(2 * Math.PI * frequency * t);
      channelData[i] = wave * envelope * 0.8;
    }

    this.sounds.set(name, buffer);
  }

  private generateVictorySound() {
    if (!this.audioContext) return;

    const sampleRate = this.audioContext.sampleRate;
    const duration = 1.0;
    const numSamples = Math.floor(sampleRate * duration);
    const buffer = this.audioContext.createBuffer(1, numSamples, sampleRate);
    const channelData = buffer.getChannelData(0);

    const notes = [523, 659, 784, 1047]; // C, E, G, alta C

    for (let i = 0; i < numSamples; i++) {
      const t = i / sampleRate;
      const noteIndex = Math.min(Math.floor(t * 4), notes.length - 1);
      const frequency = notes[noteIndex];

      const envelope = Math.exp(-t * 1.5);
      const wave = Math.sign(Math.sin(2 * Math.PI * frequency * t));
      channelData[i] = wave * envelope * 0.75;
    }

    this.sounds.set('victory', buffer);
  }

  private generateDefeatSound() {
    if (!this.audioContext) return;

    const sampleRate = this.audioContext.sampleRate;
    const duration = 0.8;
    const numSamples = Math.floor(sampleRate * duration);
    const buffer = this.audioContext.createBuffer(1, numSamples, sampleRate);
    const channelData = buffer.getChannelData(0);

    const notes = [400, 300, 200, 150]; // Melodía descendente

    for (let i = 0; i < numSamples; i++) {
      const t = i / sampleRate;
      const noteIndex = Math.min(Math.floor(t * 4), notes.length - 1);
      const frequency = notes[noteIndex];

      const envelope = Math.exp(-t * 2);
      const wave = Math.sign(Math.sin(2 * Math.PI * frequency * t));
      channelData[i] = wave * envelope * 0.75;
    }

    this.sounds.set('defeat', buffer);
  }

  private generateConnectSound() {
    if (!this.audioContext) return;

    const sampleRate = this.audioContext.sampleRate;
    const duration = 0.5;
    const numSamples = Math.floor(sampleRate * duration);
    const buffer = this.audioContext.createBuffer(1, numSamples, sampleRate);
    const channelData = buffer.getChannelData(0);

    for (let i = 0; i < numSamples; i++) {
      const t = i / sampleRate;
      // Tono ascendente para conectar
      const frequency = 300 + t * 600;
      const envelope = Math.exp(-t * 3);
      const wave = Math.sign(Math.sin(2 * Math.PI * frequency * t));
      channelData[i] = wave * envelope * 0.75;
    }

    this.sounds.set('connect', buffer);
  }

  playSound(name: string) {
    const ctx = this.getAudioContext();
    if (!ctx) return;

    // Reanudar si está suspendido (política de autoplay)
    if (ctx.state === 'suspended') {
      ctx.resume().catch(() => {});
    }

    if (!this.sounds.has(name)) return;

    try {
      const buffer = this.sounds.get(name)!;
      const source = ctx.createBufferSource();
      const gainNode = ctx.createGain();
      gainNode.gain.value = this.masterVolume;

      source.buffer = buffer;
      source.connect(gainNode);
      gainNode.connect(ctx.destination);
      source.start();
    } catch (error) {
      console.log('Error playing sound:', error);
    }
  }

  // Método para activar contexto de audio (requerido por Chrome/Safari)
  resumeAudioContext() {
    const ctx = this.getAudioContext();
    if (ctx && ctx.state === 'suspended') {
      ctx.resume().catch(() => {});
    }
  }
}

export const audioManager = new AudioManager();
